// HTML を DOMPurify でサニタイズしてから dangerouslySetInnerHTML で描画。
// Phase D: iframe を条件付許可 (YouTube / Vimeo のみ)。他 iframe は削除。
// Tailwind の prose で標準タイプグラフィ。
import DOMPurify from "dompurify";
import { useMemo } from "react";

interface Props {
  html: string | null | undefined;
  className?: string;
}

const ALLOWED_TAGS = [
  "p", "br", "span", "strong", "em", "u", "s",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "blockquote", "pre", "code",
  "a", "img",
  "hr",
  "iframe", // YouTube / Vimeo のみ、下の hook でホスト検証
  "div", // Vimeo 埋込の responsive wrapper (padding-top trick) 用
];
const ALLOWED_ATTR = [
  "href", "target", "rel",
  "src", "alt", "title",
  "width", "height",
  "style",
  "class",
  // iframe 用
  "frameborder", "allow", "allowfullscreen",
  "referrerpolicy", "loading",
];

// iframe src は YouTube / Vimeo のみ許可。他は iframe 要素ごと削除。
const IFRAME_SRC_WHITELIST = [
  /^https:\/\/(?:www\.)?youtube\.com\/embed\/[\w-]+/i,
  /^https:\/\/player\.vimeo\.com\/video\/\d+/i,
];

let HOOKS_REGISTERED = false;
function ensureHooks() {
  if (HOOKS_REGISTERED) return;
  HOOKS_REGISTERED = true;
  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName === "iframe") {
      const src = (node as Element).getAttribute?.("src") ?? "";
      const ok = IFRAME_SRC_WHITELIST.some((re) => re.test(src));
      if (!ok) {
        (node as Element).parentNode?.removeChild(node);
      }
    }
  });
}

function sanitize(html: string): string {
  ensureHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ADD_ATTR: ["target"],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ["script", "style", "object", "embed", "form", "input"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
  });
}

export function RichTextRender({ html, className }: Props) {
  const safe = useMemo(() => sanitize(html ?? ""), [html]);
  if (!safe) return null;
  return (
    <div
      className={className ?? "prose prose-sm max-w-none"}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
